<template>
    <div class="kgr-grid kgr-grid--full kgr-grid--large-1of2 kgr-grid--gutters kgr-grid--bigGutters">
        <div class="kgr-grid__cell">
            <kgr-form-row
                :label="$t('Beitrag')"
                :error="error"
                field-name="name"
                required
            >
                <kgr-text-input v-model="clubFee.name" />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('Kategorie')"
                :error="error"
                required
                field-name="category"
            >
                <kgr-club-fee-category-select
                    v-model="clubFee.category"
                    :club-id="clubId"
                    hide-label
                />
            </kgr-form-row>
            <kgr-form-row
                :error="error"
                field-name="department"
            >
                <kgr-department-select
                    v-model="clubFee.department"
                    :club-id="clubId"
                    clearable
                />
            </kgr-form-row>
            <kgr-form-row
                :error="error"
                field-name="clubCostCenter"
            >
                <kgr-club-cost-center-select
                    v-model="clubFee.clubCostCenter"
                    :club-id="clubId"
                    :department-id="clubFee.department ? clubFee.department.id : null"
                />
                <kgr-icon
                    icon-class="info-circle"
                    small
                    style="position:relative; top: 5px;"
                /> {{ $t('club.accounting.clubFee.clubCostCenter.info') }}
            </kgr-form-row>
            <kgr-form-row
                v-if="impersonalAccounts.length > 0"
                :error="error"
                field-name="impersonalAccount"
            >
                <kgr-club-impersonal-account-select
                    v-model="clubFee.impersonalAccount"
                    :club-id="clubId"
                />
            </kgr-form-row>
            <kgr-form-row
                v-if="invoiceTemplateTypes.length > 1"
                :label="$t('Rechnungsvorlage')"
                :error="error"
                field-name="invoiceTemplateType"
            >
                <kgr-generic-select
                    v-model="invoiceTemplateTypeOption"
                    display="name"
                    no-null-option
                    :loading="loading"
                    :options="invoiceTemplateTypes"
                />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('Zusätzlicher Rechnungshinweis')"
                :error="error"
                field-name="note"
            >
                <kgr-text-input
                    v-model="clubFee.note"
                    :max-length="255"
                />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('zu zahlen')"
                :error="error"
                field-name="paymentApproach"
            >
                <kgr-generic-radio
                    v-model="clubFee.paymentApproach"
                    :options="paymentApproachOptions"
                />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('Zahlungsintervall')"
                :error="error"
                field-name="type"
            >
                <kgr-generic-select
                    v-model="clubFeeIntervalSelection"
                    display="text"
                    no-null-option
                    :options="paymentIntervals"
                    @post-input="onPaymentIntervalChange"
                />
            </kgr-form-row>
            <div v-if="clubFeeIntervalSelection">
                <kgr-form-row
                    v-if="clubFeeIntervalSelection.value === 'monthly'"
                    :label="$t('Tag')"
                    field-name="paymentInterval"
                    :error="error"
                >
                    <kgr-generic-select
                        v-model="clubFee.paymentInterval.monthly.day"
                        display="text"
                        no-null-option
                        :options="days"
                    />
                </kgr-form-row>
                <kgr-form-row
                    v-if="clubFeeIntervalSelection.value === 'monthlyEnd'"
                    :label="$t('Tage vor Monatsende')"
                    field-name="paymentInterval"
                    :error="error"
                >
                    <kgr-generic-select
                        v-model="clubFee.paymentInterval.monthlyEnd.offset"
                        display="text"
                        no-null-option
                        :options="dayOffset"
                    />
                </kgr-form-row>
                <kgr-form-row
                    v-if="clubFeeIntervalSelection.value === 'quarterly'"
                    :label="$t('Tage nach Quartalsbeginn')"
                    field-name="paymentInterval"
                    :error="error"
                >
                    <kgr-generic-select
                        v-model="clubFee.paymentInterval.quarterly.offsetDay"
                        display="text"
                        no-null-option
                        :options="dayOffset"
                    />
                </kgr-form-row>
                <kgr-form-row
                    v-if="clubFeeIntervalSelection.value === 'quarterly'"
                    :label="$t('Monate')"
                    field-name="paymentInterval"
                    :error="error"
                >
                    <kgr-generic-select
                        v-model="clubFee.paymentInterval.quarterly.offsetMonth"
                        display="text"
                        no-null-option
                        :options="quarters"
                    />
                </kgr-form-row>
                <kgr-form-row
                    v-if="clubFeeIntervalSelection.value === 'halfYearly'"
                    :label="$t('Tage nach Halbjahresbeginn')"
                    field-name="dayOffset"
                    :error="error"
                >
                    <kgr-generic-select
                        v-model="clubFee.paymentInterval.halfYearly.offsetDay"
                        display="text"
                        no-null-option
                        :options="dayOffset"
                    />
                </kgr-form-row>
                <kgr-form-row
                    v-if="clubFeeIntervalSelection.value === 'halfYearly'"
                    :label="$t('Monate nach Halbjahresbeginn')"
                    field-name="paymentInterval"
                    :error="error"
                >
                    <kgr-generic-select
                        v-model="clubFee.paymentInterval.halfYearly.offsetMonth"
                        display="text"
                        no-null-option
                        :options="monthsHalfYearly"
                    />
                </kgr-form-row>
                <kgr-form-row
                    v-if="clubFeeIntervalSelection.value === 'yearly'"
                    :label="$t('Tag')"
                    field-name="paymentInterval"
                    :error="error"
                >
                    <kgr-generic-select
                        v-model="clubFee.paymentInterval.yearly.day"
                        display="text"
                        no-null-option
                        :options="days"
                    />
                </kgr-form-row>
                <kgr-form-row
                    v-if="clubFeeIntervalSelection.value === 'yearly'"
                    :label="$t('Monat')"
                    field-name="paymentInterval"
                    :error="error"
                >
                    <kgr-generic-select
                        v-model="clubFee.paymentInterval.yearly.month"
                        display="text"
                        no-null-option
                        :options="months"
                    />
                </kgr-form-row>
                <kgr-form-row
                    v-if="clubFeeIntervalSelection.value === 'rolling'"
                    :label="$t('Intervall')"
                    field-name="paymentInterval"
                    :error="error"
                >
                    <kgr-generic-select
                        v-model="clubFee.rollingIntervalType"
                        display="text"
                        no-null-option
                        :options="rollingIntervalTypes"
                    />
                </kgr-form-row>
            </div>
        </div>
        <div class="kgr-grid__cell">
            <div v-if="!editMode">
                <kgr-form-row
                    :label="$t('Betrag')"
                    :error="error"
                    field-name="amount"
                >
                    <kgr-number-input
                        v-model="clubFee.basePrice.amount"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Währung')"
                    :error="error"
                    field-name="currency"
                >
                    <kgr-generic-select
                        v-model="clubFee.basePrice.currency"
                        no-null-option
                        :options="currencies"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('zu zahlen')"
                    :error="error"
                    field-name="type"
                >
                    <kgr-generic-select
                        v-model="clubFee.basePrice.type"
                        display="text"
                        no-null-option
                        :options="types"
                        @post-input="onPriceTypeChange"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Steuersatz in %')"
                    :error="error"
                    field-name="vat"
                >
                    <kgr-number-input
                        v-model="clubFee.basePrice.vat"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Brutto/Netto')"
                    :error="error"
                    field-name="vat"
                >
                    <kgr-generic-radio
                        v-model="clubFee.basePrice.netOrGross"
                        :options="netOrGrossOptions"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Lädt Guthabenkonto auf')"
                    :error="error"
                    field-name="chargeCredit"
                >
                    <kgr-checkbox
                        v-model="clubFee.chargeCredit"
                    />
                </kgr-form-row>
            </div>
        </div>
    </div>
</template>

<script>
import AccountingClubFee from '../../../js/dto/AccountingClubFee.js';
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import ClubCostCenterSelect from '../../common/form/select/ClubCostCenterSelect.vue';
import ClubFeeCategorySelect from '../../../components/common/form/select/ClubFeeCategorySelect.vue';
import ClubImpersonalAccountRepository from '../../../js/repository/club/accounting/ClubImpersonalAccountRepository.js';
import ClubImpersonalAccountSelect from '../../common/form/select/ClubImpersonalAccountSelect.vue';
import ClubInvoiceTemplateRepository from '../../../js/repository/ClubInvoiceTemplateRepository.js';
import DepartmentSelect from '../../../components/common/form/select/DepartmentSelect.vue';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import GenericRadio from '../../../components/common/form/radio/GenericRadio.vue';
import GenericSelect from '../../../components/common/form/select/GenericSelect.vue';
import Icon from '../../common/icon/Icon.vue';
import NumberInput from '../../../components/common/form/input/NumberInput.vue';
import TextInput from '../../../components/common/form/input/TextInput.vue';

export default {
    components: {
        'kgr-checkbox': Checkbox,
        'kgr-club-cost-center-select': ClubCostCenterSelect,
        'kgr-club-fee-category-select': ClubFeeCategorySelect,
        'kgr-club-impersonal-account-select': ClubImpersonalAccountSelect,
        'kgr-department-select': DepartmentSelect,
        'kgr-form-row': FormRow,
        'kgr-generic-radio': GenericRadio,
        'kgr-generic-select': GenericSelect,
        'kgr-icon': Icon,
        'kgr-number-input': NumberInput,
        'kgr-text-input': TextInput,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        clubFeeEdit: {
            type: [AccountingClubFee, Object],
            required: true,
        },
        editMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        error: {
            type: Error,
            required: true,
        },
    },
    data() {
        return {
            clubFee: this.clubFeeEdit,
            loading: false,
            impersonalAccounts: [],
            invoiceTemplateTypes: [],
            clubFeeIntervalSelection: this.clubFeeEdit.paymentInterval?.type || '',
            currencies: [
                'EUR',
                'USD',
            ],
            months: [
                { value: 1, text: this.$t('Januar') },
                { value: 2, text: this.$t('Februar') },
                { value: 3, text: this.$t('März') },
                { value: 4, text: this.$t('April') },
                { value: 5, text: this.$t('Mai') },
                { value: 6, text: this.$t('Juni') },
                { value: 7, text: this.$t('Juli') },
                { value: 8, text: this.$t('August') },
                { value: 9, text: this.$t('September') },
                { value: 10, text: this.$t('Oktober') },
                { value: 11, text: this.$t('November') },
                { value: 12, text: this.$t('Dezember') },
            ],
            days: Array.from({ length: 28 }, (_, i) => ({ value: i + 1, text: `${i + 1}.` })),
            dayOffset: Array.from({ length: 28 }, (_, i) => ({ value: i, text: i })),
            quarters: [
                { value: 0, text: this.$t('Jan / Apr / Jul / Okt') },
                { value: 1, text: this.$t('Feb / Mai / Aug / Nov') },
                { value: 2, text: this.$t('Mär / Jun / Sep / Dez') },
            ],
            monthsHalfYearly: [
                { value: 0, text: this.$t('Jan / Jul') },
                { value: 1, text: this.$t('Feb / Aug') },
                { value: 2, text: this.$t('Mär / Sep') },
                { value: 3, text: this.$t('Apr / Okt') },
                { value: 4, text: this.$t('Mai / Nov') },
                { value: 5, text: this.$t('Jun / Dez') },
            ],
            types: [
                { value: 'oneTime', text: this.$t('einmalig') },
                { value: 'perDay', text: this.$t('pro Tag') },
                { value: 'perWeek', text: this.$t('pro Woche') },
                { value: 'perMonth', text: this.$t('pro Monat') },
                { value: 'perQuarter', text: this.$t('pro Quartal') },
                { value: 'perHalfYear', text: this.$t('pro Halbjahr') },
                { value: 'perYear', text: this.$t('pro Jahr') },
                { value: 'perThreeYears', text: this.$t('pro 3 Jahre') },
            ],
            netOrGrossOptions: {
                Brutto: 'gross',
                Netto: 'net',
            },
            paymentApproachOptions: {
                vorschüssig: 'in_advance',
                nachschüssig: 'in_arrears',
                'nach Kategorie': 'undefined',
            },
        };
    },
    computed: {
        paymentIntervals() {
            const type = this.clubFee.basePrice.type.value || this.clubFee.basePrice.type;

            switch (type) {
                case 'perYear':
                    return [
                        { value: 'yearly', text: this.$t('jährlich') },
                        { value: 'rolling', text: this.$t('rollierend') },
                    ];
                case 'perThreeYears':
                    return [
                        { value: 'rolling', text: this.$t('rollierend') },
                    ];
                case 'perMonth':
                    return [
                        { value: '', text: this.$t('nach Kundenwunsch') },
                        { value: 'monthly', text: this.$t('monatlich') },
                        { value: 'monthlyEnd', text: this.$t('zum Monatsende') },
                        { value: 'quarterly', text: this.$t('quartalsweise') },
                        { value: 'halfYearly', text: this.$t('halbjährlich') },
                        { value: 'yearly', text: this.$t('jährlich') },
                        { value: 'rolling', text: this.$t('rollierend') },
                    ];
                case 'perQuarter':
                    return [
                        { value: 'quarterly', text: this.$t('quartalsweise') },
                        { value: 'halfYearly', text: this.$t('halbjährlich') },
                        { value: 'yearly', text: this.$t('jährlich') },
                    ];
                case 'perHalfYear':
                    return [
                        { value: 'halfYearly', text: this.$t('halbjährlich') },
                        { value: 'yearly', text: this.$t('jährlich') },
                    ];
                default:
                    return [
                        { value: '', text: this.$t('nach Kundenwunsch') },
                        { value: 'monthly', text: this.$t('monatlich') },
                        { value: 'monthlyEnd', text: this.$t('zum Monatsende') },
                        { value: 'quarterly', text: this.$t('quartalsweise') },
                        { value: 'halfYearly', text: this.$t('halbjährlich') },
                        { value: 'yearly', text: this.$t('jährlich') },
                    ];
            }
        },
        rollingIntervalTypes() {
            const type = this.clubFee.basePrice.type.value || this.clubFee.basePrice.type;
            switch (type) {
                case 'perThreeYears':
                    return [
                        { value: 'three_yearly', text: this.$t('dreijährlich') },
                    ];
                case 'perYear':
                    return [
                        { value: 'yearly', text: this.$t('jährlich') },
                    ];
                case 'perMonth':
                    return [
                        { value: 'monthly', text: this.$t('monatlich') },
                    ];
                default:
                    return [];
            }
        },
        invoiceTemplateTypeOption: {
            get() {
                return this.invoiceTemplateTypes.find(
                    (option) => option.type === this.clubFee.invoiceTemplateType,
                ) || null;
            },
            set(selectedOption) {
                this.clubFee.invoiceTemplateType = selectedOption ? selectedOption.type : null;
            },
        },
    },
    watch: {
        clubFee: {
            deep: true,
            handler() {
                this.$emit('update', this.clubFee);
            },
        },
    },
    created() {
        this.loadImpersonalAccounts();
        this.loadInvoiceTemplateTypes();
        if (this.editMode) {
            this.clubFeeIntervalSelection = this.clubFee.rollingIntervalType !== null ? this.paymentIntervals.find((i) => i.value === 'rolling') : this.paymentIntervals.find((i) => i.value === this.clubFee.paymentInterval?.type);
        } else {
            this.clubFeeIntervalSelection = this.paymentIntervals[0];
        }
        if (this.editMode && this.clubFee?.paymentInterval) {
            this.onPaymentIntervalChange();
        }
    },
    methods: {
        onPriceTypeChange() {
            this.clubFee.paymentInterval.type = '';
            this.clubFee.rollingIntervalType = null;
            this.clubFeeIntervalSelection = this.paymentIntervals[0];
            this.onPaymentIntervalChange();
        },
        onPaymentIntervalChange() {
            this.clubFee.paymentInterval.type = this.clubFeeIntervalSelection?.value && this.clubFeeIntervalSelection?.value !== 'rolling' ? this.clubFeeIntervalSelection?.value : '';
            const pi = this.clubFee.paymentInterval;

            // Monthly
            if (pi.monthly?.day != null) {
                pi.monthly.day = this.days.find((i) => i.value === pi.monthly.day) || this.days[0];
            }

            // Monthly End
            if (pi.monthlyEnd?.offset != null) {
                pi.monthlyEnd.offset = this.dayOffset.find((i) => i.value === pi.monthlyEnd.offset) || this.dayOffset[0];
            }

            // Quarterly
            if (pi.quarterly?.offsetDay != null) {
                pi.quarterly.offsetDay = this.dayOffset.find((i) => i.value === pi.quarterly.offsetDay) || this.dayOffset[0];
            }
            if (pi.quarterly?.offsetMonth != null) {
                pi.quarterly.offsetMonth = this.quarters.find((i) => i.value === pi.quarterly.offsetMonth) || this.quarters[0];
            }

            // Half-Yearly
            if (pi.halfYearly?.offsetDay != null) {
                pi.halfYearly.offsetDay = this.dayOffset.find((i) => i.value === pi.halfYearly.offsetDay) || this.dayOffset[0];
            }
            if (pi.halfYearly?.offsetMonth != null) {
                pi.halfYearly.offsetMonth = this.monthsHalfYearly.find((i) => i.value === pi.halfYearly.offsetMonth) || this.monthsHalfYearly[0];
            }

            // Yearly
            if (pi.yearly?.day != null) {
                pi.yearly.day = this.days.find((i) => i.value === pi.yearly.day) || this.days[0];
            }
            if (pi.yearly?.month != null) {
                pi.yearly.month = this.months.find((i) => i.value === pi.yearly.month) || this.months[0];
            }
            if (this.clubFeeIntervalSelection?.value === 'rolling') {
                this.clubFee.rollingIntervalType = this.rollingIntervalTypes[0];
            }

            this.clubFee.paymentInterval = pi;
        },
        loadInvoiceTemplateTypes() {
            this.loading = true;
            ClubInvoiceTemplateRepository.findByClub(this.clubId).then((response) => {
                this.loading = false;
                this.initialLoading = false;
                this.invoiceTemplateTypes = response.filter((t) => t.active);
            });
        },
        loadImpersonalAccounts() {
            this.loading = true;
            ClubImpersonalAccountRepository.findAll(this.clubId)
                .then((data) => {
                    this.impersonalAccounts = data;
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss"></style>
